import { defineStore } from 'pinia';
import { workflowApiService } from 'src/services';
import { formBuilderApiService } from 'src/services';
import { docFieldsApiService } from 'src/services';
import displayFieldLabel from 'src/utils/displayFieldLabel';
import { WorkFlowBuilder } from 'src/pages/WorkflowBuilder/work-flow-builder.js';
import { CONSTANTS } from 'src/constants';
import WorkflowVariableService from 'src/services/workflow/WorkflowVariableService';

export interface WorkflowCard {
  text: string;
  card_version: number;
  latest_version: number;
  deprecated: boolean;
  svg_background_color?: string;
}

export interface WorkflowCategory {
  category: string;
  cards: WorkflowCard[];
  svg_background_color?: string;
}

// In-flight guard for loadAllDocumentFieldNames: concurrent first-mounts
// (a card with several field inputs, or several cards at once) share this
// one request instead of each firing its own. Kept module-level so it is
// not part of reactive store state.
let allDocumentFieldNamesRequest: Promise<void> | null = null;

export const useWorkflowBuilderStore = defineStore('workflow_builder_store', {
  state: () => ({
    docFields: [] as any,
    // Deduped union of every doc type's layout field names, used to hint
    // the advanced workflow's field-name inputs. The advanced flow isn't
    // bound to a single doc type, so we pool across all of them.
    allDocumentFieldNames: [] as string[],
    selectedRowIndex: 0,
    workflowBuilderModel: [] as any,
    cardType: '',
    cards: [] as WorkflowCategory[],
    category: [] as WorkflowCategory[],
    allCardsListForCardType: [] as WorkflowCard[],
    filteredCardsList: {
      cards: [] as WorkflowCard[],
      category: [] as any,
      cardType: '',
    },
    selectedSubCategory: false,
    activeSubCategory: '',
    showItemPanel: false,

    //  new implemenation
    search_value: '',
    triggerOn: {} as any,
    conditions: [] as any,
    actions: [] as any,

    selectedWhenCards: {} as any,
    selectedAndCards: [] as any,
    selectedThenCards: [] as any,

    selectedAdditionalConditions: [] as any[],
    selectedAdditionalExecutions: [] as any[],

    workflowAndCards: [] as any,
    workflowWhenCards: [] as any,
    workflowThenCards: [] as any,

    workflowModel: {} as any,

    cardToTest: {} as any,
    docOperatorVariables: {} as any,
    docOperatorVariablesSensitive: {} as any,
    docOperatorLockedSensitiveData: {} as any,

    // User-defined workflow variables (Homey-style) — synced from designer
    workflowVariables: [] as Array<{ id: string; name: string; var_type: string; default_value: any; value: any }>,
  }),
  actions: {
    async loadWorkflowVariables() {
      try {
        const service = new WorkflowVariableService();
        this.workflowVariables = await service.getAll();
      } catch (e) {
        console.error('Failed to load workflow variables:', e);
      }
    },
    async loadDocumentDefaultFields(doc_type: string) {
      if (this.docFields.length < 1) {
        const response =
          await formBuilderApiService.loadDocumentDefaultFields(doc_type);

        if (response.field_setting.length > 0) {
          Array.from(response.field_setting).forEach((group: any) => {
            Array.from(group.rows).forEach((rows: any) => {
              Array.from(rows).forEach((row: any) => {
                row.label = displayFieldLabel.methods.getLabelForFormBuilder(
                  row,
                  '',
                );
                this.docFields.push(
                  Object.assign(
                    {},
                    {
                      value: row.field_name,
                      label: row.label,
                    },
                  ),
                );
              });
            });
          });
        }
      }
    },

    // Pool the field names from every doc type's layout (one request) so
    // the field-name inputs can hint regardless of which document the
    // workflow ends up running on. Loaded once; failures are non-fatal —
    // the input still accepts any typed value without hints.
    async loadAllDocumentFieldNames() {
      if (this.allDocumentFieldNames.length > 0) {
        return;
      }
      if (!allDocumentFieldNamesRequest) {
        allDocumentFieldNamesRequest = (async () => {
          try {
            const response =
              await docFieldsApiService.getFieldsForAllDocTypes();
            const settingsByDocType = response?.documents_field_settings || {};
            const seen = new Set<string>();
            Object.values(settingsByDocType).forEach((groups: any) => {
              Array.from(groups || []).forEach((group: any) => {
                Array.from(group?.rows || []).forEach((row: any) => {
                  Array.from(row || []).forEach((field: any) => {
                    const fieldName = field?.field_name;
                    if (fieldName) {
                      seen.add(fieldName);
                    }
                  });
                });
              });
            });
            this.allDocumentFieldNames = Array.from(seen).sort((a, b) =>
              a.localeCompare(b),
            );
          } catch (e) {
            console.error('Failed to load document field names for hints:', e);
          }
        })();
      }
      try {
        await allDocumentFieldNamesRequest;
      } finally {
        allDocumentFieldNamesRequest = null;
      }
    },

    loadDefaultWorkFlowJson() {
      if (!this.workflowBuilderModel || this.workflowBuilderModel.length < 1) {
        this.addCriteria(this.selectedRowIndex);
      }
    },

    addRowToCondition(index: number) {
      this.workflowBuilderModel[index].criteria.condition_elements.push(
        WorkFlowBuilder.data().condition_fields,
      );
    },

    addRowToActions(index: number) {
      this.workflowBuilderModel[index].action.action_elements.push(
        WorkFlowBuilder.data().action_fields,
      );
    },

    addRowToActionsForStatusChange(index: number) {
      this.workflowBuilderModel[index].action.action_elements.push({
        status: '',
      });
    },

    addCriteria(index: number) {
      if (index >= this.workflowBuilderModel.length) {
        this.workflowBuilderModel.push(WorkFlowBuilder.data().fields);
      } else {
        this.workflowBuilderModel.splice(
          index,
          0,
          WorkFlowBuilder.data().fields,
        );
      }
      this.addRowToCondition(index);
      this.addRowToActions(index);
    },

    deleteCriteria(index: number) {
      if (this.workflowBuilderModel.length == 1) {
        this.workflowBuilderModel.splice(index, 1);
        this.addCriteria(this.selectedRowIndex);
        return false;
      }
      this.workflowBuilderModel.splice(index, 1);

      return true;
    },

    async getWorkflowAllCards() {
      await this.loadWorkflowAllCards();
    },

    async loadWorkflowAllCards() {
      try {
        const workflowAllCards = await workflowApiService.getWorkflowAllCards();
        this.setWorkflowAllCards(workflowAllCards);
      } catch (err) {
        console.error('[WorkflowBuilder] loadWorkflowAllCards failed:', err);
      }
    },

    setWorkflowAllCards(workflowAllCards: any) {
      if (workflowAllCards.when_cards) {
        this.workflowWhenCards = workflowAllCards.when_cards;
      }
      if (workflowAllCards.and_cards) {
        this.workflowAndCards = workflowAllCards.and_cards;
      }
      if (workflowAllCards.then_cards) {
        this.workflowThenCards = workflowAllCards.then_cards;
      }
    },

    setDocOperatorVariables(docOperator: any, variable_uuid: string) {
      let script_data_uuid: string | null = null;
      main_loop_script_data_uuid: for (const then_card of this
        .selectedThenCards) {
        for (const then_var of then_card.variables) {
          if (then_var.uuid != variable_uuid) {
            continue;
          }
          for (const matched_var of then_card.variables) {
            //only know uuid of script variable, need uuid of script_data variable
            if (matched_var.data_type.includes('script_data')) {
              script_data_uuid = matched_var.uuid;
              break main_loop_script_data_uuid;
            }
          }
        }
      }
      if (script_data_uuid) {
        // Clear previous script_data variables when switching scripts
        this.docOperatorVariables[script_data_uuid] = {};
        this.docOperatorVariablesSensitive[script_data_uuid] = [];
        const docOperatorVariablesDict = JSON.parse(docOperator.variables); //JSON string of Object
        for (const variable_key of Object.keys(docOperatorVariablesDict)) {
          this.docOperatorVariables[script_data_uuid][variable_key] = '';
        }
        if (docOperator.sensitive_variables) {
          for (const sensitive_variable_key of docOperator.sensitive_variables) {
            this.docOperatorVariablesSensitive[script_data_uuid].push(sensitive_variable_key);
          }
        }
      }
    },
    getCardsByType(cardType: string) {
      let selectedCategories: WorkflowCategory[] = [];

      if (cardType === CONSTANTS.when) {
        selectedCategories = this.workflowWhenCards;
      } else if (cardType === CONSTANTS.and) {
        selectedCategories = this.workflowAndCards;
      } else if (cardType === CONSTANTS.then) {
        selectedCategories = this.workflowThenCards;
      }
      this.cardType = cardType;
      this.category = selectedCategories;
      this.filteredCardsList.category = selectedCategories;
      return selectedCategories;
    },

    getAllCards() {
      this.allCardsListForCardType = [];

      for (const category_card of this.category) {
        this.setSubCategory(category_card, true); // Pass flag to avoid filtering/searching
        const sub_category_cards = this.filteredCardsList.cards;
        for (const sub_category_card of sub_category_cards) {
          sub_category_card.svg_background_color =
            category_card.svg_background_color;
          this.allCardsListForCardType.push(sub_category_card);
        }
      }

      this.selectedSubCategory = false;
      return this.allCardsListForCardType;
    },

    setSubCategory(cards: any, isInternalCall = false) {
      if (this.cards === cards && this.selectedSubCategory) {
        this.selectedSubCategory = false;
        this.activeSubCategory = '';
        return;
      }

      this.selectedSubCategory = true;
      this.activeSubCategory = cards.category;

      if (cards) {
        this.cards = cards;
        this.filteredCardsList.cards = cards.cards;
        this.filterDeprecatedCards();
        this.filterLatestCardVersion();
      }

      if (!isInternalCall) {
        this.getSearchResults(this.search_value);
      }
    },

    filterLatestCardVersion() {
      this.filteredCardsList.cards = this.filteredCardsList.cards.filter(
        (card: any) => Number(card.card_version) === Number(card.latest_version),
      );
    },

    filterDeprecatedCards() {
      this.filteredCardsList.cards = this.filteredCardsList.cards.filter(
        (card: any) => card.deprecated == false,
      );
    },
    getSearchResults(search_value: string) {
      if (search_value.length > 2 && this.selectedSubCategory) {
        const selectedCategory = this.cards.find(
          (category) => category.category === this.activeSubCategory,
        );

        if (selectedCategory) {
          this.filteredCardsList.cards = selectedCategory.cards.filter(
            (card: WorkflowCard) =>
              card.text.toLowerCase().includes(search_value.toLowerCase()),
          );
        }
      } else if (this.selectedSubCategory) {
        const selectedCategory = this.cards.find(
          (category) => category.category === this.activeSubCategory,
        );

        if (selectedCategory) {
          this.filteredCardsList.cards = selectedCategory.cards;
        }
      } else {
        this.filteredCardsList.cards = this.allCardsListForCardType;
      }

      return this.filteredCardsList.cards;
    },
  },
});